import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import styled from 'styled-components';
import { isEmpty } from 'lodash-es';
import {
  BaseBox,
  FlexBox,
  CenterBox,
  Heading2,
  Heading3,
  DoconInput,
  DoconInputWrapper,
  Icons,
} from '@docon/docon-ui-library';
import { getDashboardData } from 'redux/reducer/dashboard';
import Layout from 'components/Layout';
import Card from 'components/Records/Card';
import Prescription from 'components/Records/Prescription';
import AppointmentCard from 'components/Appointment/components/Card';
import DoctorsCard from 'components/Doctors/Card';
import Appointment from 'components/Appointment/modal/details';
import { colors } from 'utilities/colorConfig';
import { Loader, Image } from 'shared';
import { cleverTapAnl } from 'utilities';
import { EventDoctorSelect } from 'utilities/clevertapEvent';
import { getUserDetailsFromToken } from 'redux/reducer/auth';

export type IProps = RouteComponentProps;

const Title = styled(FlexBox)`
  margin-bottom: 1rem;
  font-weight: 600;
  justify-content: space-between;
`;

const Content = styled(BaseBox)`
  height: calc(100% - 100px);

  ${DoconInputWrapper} {
    background-color: ${colors.white};
    --inp-height: 45px;
  }

  .zeroCase {
    padding: 16px;
    height: inherit;
    text-align: center;
    place-content: center;

    ${Image} { max-width: 100%; }
  }
`;

enum ITEM_PATHNAME {
  appointment = 'appointment',
  doctors = 'doctors',
  prescription = 'records',
}

function Item({
  title, id, data, count, onShowAllClick, onCardClick,

}: any) {

  if (isEmpty(data)) return null;

  function handleCardClick(appointment) {
    onCardClick({ type: id, value: appointment });
  }

  function handleVisitCardClick({ visit }: any) {
    onCardClick({ type: id, value: visit });
  }

  function handleDocClick({ shortUrl, rank, doctorID }: any) {
    cleverTapAnl.event({
      event_name: 'doctor_select',
      event: {
        page_name: 'home',
        is_search_active: false,
        search_term: '',
        doctor_id: doctorID,
        rank,
      },
    } as EventDoctorSelect);
    onCardClick({ type: id, value: shortUrl });
  }

  function renderLists() {
    switch (id) {
      case ITEM_PATHNAME.appointment:
        return (
          data.map((appointment: any) => (
            <AppointmentCard
              key={appointment.appointmentID}
              appointment={appointment}
              onCardClick={handleCardClick}
            />
          ))
        );
      case ITEM_PATHNAME.prescription:
        return (
          data.map((visit: any) => (
            <Card
              key={visit.visitID}
              visit={visit}
              onClick={handleVisitCardClick}
            />
          ))
        );
      case ITEM_PATHNAME.doctors:
        return (
          data.map((doctor: any, index: number) => (
            <DoctorsCard
              key={doctor.doctorID}
              rank={index + 1}
              pageName='home'
              doctor={doctor}
              onClick={handleDocClick}
            />
          ))
        );
    }

  }

  return (
    <BaseBox mb='12px'>
      <Title>
        <Heading2 title={title} />
        <Heading3
          id={id}
          title={count > 1 ? 'Show all' : ''}
          className='clickable'
          color={colors.primaryBlue}
          onClick={onShowAllClick}
        />
      </Title>
      {renderLists()}
    </BaseBox>
  );
}

function HomeComponent({ history }: IProps) {
  const [activeAppointment, setActiveAppointment] = useState<any>({});
  const [activeVisit, setActiveVisit] = useState<any>({});

  const { user, accessToken } = useSelector(state => state.auth);
  const { isAppointmentCanceled, closingBookingModal } = useSelector(state => state.appointment);
  const { isWebView } = useSelector(state => state.app);
  const {
    appointments, visits, doctors, count, isFetching,
  } = useSelector(state => state.dashboard);
  const dispatch = useDispatch();

  useEffect(() => {
    // Check if the current environment is a web view, the user has no mobile number, and an access token exists
    if (isWebView && !user.mobileNumber && accessToken) {
      // Dispatch an action to fetch user details using the access token
      dispatch(getUserDetailsFromToken(accessToken));
    }
  }, [isWebView, user, accessToken]);

  useEffect(() => {
    fetchData();
  }, [user]);

  useEffect(() => {
    if (isAppointmentCanceled || closingBookingModal) {
      fetchData();
      setActiveAppointment({});
    }
  }, [isAppointmentCanceled, closingBookingModal]);

  function fetchData() {
    dispatch(getDashboardData(user.userID));
  }

  function handleShowAllClick(event: any) {
    event.stopPropagation();
    const id = event?.currentTarget?.id;
    if (id)
      history.push(`/${id}`);
  }

  function handleCardClick({ type, value }) {
    switch (type) {
      case ITEM_PATHNAME.appointment:
        return setActiveAppointment(value);
      case ITEM_PATHNAME.prescription:
        return setActiveVisit(value);
      case ITEM_PATHNAME.doctors:
        return history.push(`/doctors/${value}`);
    }
  }

  const handleModalClose = (modal: string) => () => {
    switch (modal) {
      case 'prescription':
        return setActiveVisit({});
      case 'appointment':
        return setActiveAppointment({});
      default:
        break;
    }
  };

  function handleSearchClick() {
    history.push('/doctors/search');
  }

  function renderContent() {
    const isEmptyState = isEmpty(appointments) && isEmpty(doctors) && isEmpty(visits);

    if (isEmptyState)
      return (
        <Content>
          <DoconInput
            name='search'
            value=''
            placeholder='Search and book your doctor'
            rootClassName={'rootClassName'}
            prefix={<Icons.SearchIcon /> as any}
            onClick={handleSearchClick}
          />
          <CenterBox className='zeroCase'>
            <Image src='/images/empty_search.svg' mb='5%' />
            <Heading2
              fontWeight={600}
              color={colors.tertiaryColor}
              title={'Search for your favourite doctor'}
            />
          </CenterBox>
        </Content>
      );

    return (
      <>
        <Item
          title='Appointments'
          id={ITEM_PATHNAME.appointment}
          count={count.appointments}
          data={appointments}
          onShowAllClick={handleShowAllClick}
          onCardClick={handleCardClick}
        />
        <Item
          title='Doctors'
          id={ITEM_PATHNAME.doctors}
          count={count.doctors}
          data={doctors}
          onShowAllClick={handleShowAllClick}
          onCardClick={handleCardClick}
        />
        <Item
          title='Prescriptions'
          id={ITEM_PATHNAME.prescription}
          count={count.visits}
          data={visits}
          onShowAllClick={handleShowAllClick}
          onCardClick={handleCardClick}
        />
      </>
    );
  }
  return (
    <Layout>

      {
        isFetching ? <Loader /> : renderContent()
      }

      {
        !isEmpty(activeVisit) ? (
          <Prescription
            visitID={activeVisit.visitID}
            source={'home'}
            patientID={activeVisit.patient.patientID}
            doctorID={activeVisit.doctorID}
            mobileNumber={activeVisit?.patient?.mobileNumber}
            onClose={handleModalClose('prescription')}
          />
        ) : null
      }

      {
        !isEmpty(activeAppointment) ? (
          <Appointment
            appointmentId={activeAppointment.appointmentID}
            source={'home'}
            parseAppointmentTime={activeAppointment.parseAppointmentTime}
            appointmentDate={activeAppointment.appointmentDate}
            appointmentStatus={activeAppointment.status}
            doctorID={activeAppointment.doctorID}
            clinicID={activeAppointment.clinicID}
            onClose={handleModalClose('appointment')}
          />
        ) : null
      }
    </Layout>
  );
}

export default HomeComponent;